import { getClientApiUrl, getDynamicData, post, batchEmit, createSyncObservable, createEvent } from '@avensia/excite';

export const [currentValidationErrors] = createSyncObservable<Excite.Commercetools.CartValidationErrorApiModel[]>([]);

type CommandTypes = Excite.Commercetools.CartCommandTypes['$type'];

type UnknownCommand<TType extends string> = Excite.Commercetools.CartCommand & {
  $type: TType;
  [key: string]: any;
};

type Command<TType extends string> = { $type: TType } extends { $type: CommandTypes | '' }
  ? Excite.Commercetools.CartCommandTypes
  : UnknownCommand<TType>;

const queue: Excite.Commercetools.CartCommandTypes[] = [];
let currentUpdate: Promise<Excite.Commercetools.CartApiModel | Excite.Commercetools.CartValidationApiModel> | undefined;

export interface CartCommandEventData extends Record<string, any> {
  sku?: string;
  previousQuantity?: number | undefined;
  previousLineItem?: Excite.Commercetools.LineItemApiModel;
  itemIdentifier?: string;
  additionalProductAttributes?: object;
}

export type CommandType =
  | Excite.Commercetools.CartCommandTypes
  | Excite.Commercetools.IAddLineItemCommand
  | Excite.Commercetools.IRemoveLineItemCommand
  | Excite.Commercetools.IChangeLineItemQuantityCommand;

export type CartUpdateEventType = {
  command: CommandType;
  commandEventData?: CartCommandEventData;
  cart?: Excite.Commercetools.CartApiModel;
  lineItem?: Excite.Commercetools.LineItemApiModel;
};

export function isAddLineItemCommand(command: CommandType): command is Excite.Commercetools.IAddLineItemCommand {
  return (command as Excite.Commercetools.IAddLineItemCommand).trackingType === 'addLineItem';
}

export function isRemoveLineItemCommand(command: CommandType): command is Excite.Commercetools.IRemoveLineItemCommand {
  return (command as Excite.Commercetools.IRemoveLineItemCommand).trackingType === 'removeLineItem';
}

export function isChangeLineItemQuantityCommand(
  command: CommandType,
): command is Excite.Commercetools.IChangeLineItemQuantityCommand {
  return (command as Excite.Commercetools.IChangeLineItemQuantityCommand).trackingType === 'changeLineItemQuantity';
}

export const [cartUpdateEvent] = createEvent<CartUpdateEventType>();

export function send(
  command: Excite.Commercetools.CartCommandTypes,
  commandEventData?: CartCommandEventData,
): Promise<Excite.Commercetools.CartApiModel | Excite.Commercetools.CartValidationApiModel>;

export function send<TType extends string>(
  command: Command<TType>,
  commandEventData?: CartCommandEventData,
): Promise<Excite.Commercetools.CartApiModel | Excite.Commercetools.CartValidationApiModel>;

export function send(command: Excite.Commercetools.CartCommandTypes, commandEventData?: CartCommandEventData) {
  queue.push(command);

  const cart = getDynamicData<Excite.Commercetools.CartApiModel>(
    command.cartId ? { cartId: command.cartId } : undefined,
  );
  const checkout = getDynamicData<Excite.Commercetools.CheckoutApiModel>(
    command.cartId ? { cartId: command.cartId } : undefined,
  );

  if (!currentUpdate) {
    currentUpdate = new Promise((resolve, reject) => {
      requestAnimationFrame(async () => {
        try {
          batchEmit(() => {
            checkout.loading.emit(true);
            cart.loading.emit(true);
          });

          let cartResponse: Excite.Commercetools.CartApiModel | undefined;
          let checkoutResponse: Excite.Commercetools.CheckoutApiModel | undefined;
          const validationErrors: Excite.Commercetools.CartValidationErrorApiModel[] = [];

          const queueItems: Excite.Commercetools.CartCommandTypes[] = [];

          do {
            const currentItems = queue.slice();
            queue.length = 0;
            queueItems.push(...currentItems);

            const request = await post(getClientApiUrl('ct/cart/apply'), currentItems);

            const response: Excite.Commercetools.CartCommandResponse = await request.json();
            cartResponse = response.cartResponse;
            checkoutResponse = response.checkoutResponse;
            validationErrors.push(...response.validation.validationErrors);
          } while (queue.length > 0);

          if (validationErrors.length || !cartResponse) {
            currentValidationErrors.emit(validationErrors);
            cart.loading.emit(false);
            checkout.loading.emit(false);
            resolve({ hasErrors: true, validationErrors });
          } else {
            // Emit after last update so optimistic updates doesn't get interrupted
            batchEmit(() => {
              if (cartResponse) {
                cart.emit(cartResponse);
              }

              if (checkoutResponse) {
                checkout.emit(checkoutResponse);
              }

              currentValidationErrors.emit([]);
              cart.loading.emit(false);
              checkout.loading.emit(false);

              cart.brodcastRefreshToOtherTabsAndWindows();
              checkout.brodcastRefreshToOtherTabsAndWindows();
            });

            resolve(cartResponse);
            queueItems.forEach(command => {
              const lineItem =
                'packageKey' in command
                  ? cartResponse?.items.find(i => i.sku.split('|')[0] === command.packageKey)
                  : cartResponse?.items.find(i => i.sku === commandEventData?.sku);
              cartUpdateEvent.emit({
                command,
                commandEventData,
                cart: cartResponse,
                lineItem: lineItem ?? commandEventData?.previousLineItem,
              });
            });
          }
        } catch (e) {
          reject(e);
        } finally {
          currentUpdate = undefined;
        }
      });
    });
  }

  return currentUpdate;
}
